import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Search, ArrowRight, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Guest cart helper functions
const getGuestCart = () => JSON.parse(localStorage.getItem('guestCart') || '[]');
const saveGuestCart = (cart) => localStorage.setItem('guestCart', JSON.stringify(cart));

export default function ProductsPage() {
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    let filtered = products;

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(p => p.type === selectedType);
    }

    // Filter by search
    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.benefits && p.benefits.some(b => b.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    setFilteredProducts(filtered);
  }, [products, searchTerm, selectedType]);

  const addToCart = (productId) => {
    const product = products.find(p => p.id === productId);
    
    if (user) {
      // Logged in user - use API
      axios.post(`${API}/cart`, { item_id: productId, item_type: 'product', quantity: 1 }, { withCredentials: true })
        .then(() => {
          fetchCartCount();
          toast.success('Added to cart!');
        })
        .catch(() => {
          toast.error('Failed to add to cart');
        });
    } else {
      // Guest user - use localStorage
      const guestCart = getGuestCart();
      const existingItem = guestCart.find(item => item.cart_item.item_id === productId);
      
      if (existingItem) {
        existingItem.cart_item.quantity += 1;
      } else {
        const timestamp = new Date().getTime();
        guestCart.push({
          cart_item: {
            id: `guest_${timestamp}`,
            item_id: productId,
            item_type: 'product',
            quantity: 1
          },
          item_details: {
            id: product.id,
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            type: product.type
          }
        });
      }
      
      saveGuestCart(guestCart);
      toast.success('Added to cart!');
      // Trigger cart count update
      window.dispatchEvent(new Event('guestCartUpdated'));
    }
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.88), rgba(204, 85, 0, 0.88)), url(https://images.pexels.com/photos/12336389/pexels-photo-12336389.jpeg?auto=compress&cs=tinysrgb&w=1920)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
          Tings
        </h1>
        <p className="text-xl text-cream-100 mb-8" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
          Essential Oils, Bath & Body, and Accessories
        </p>

        {/* Featured Collections */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="cursor-pointer hover:shadow-2xl transition-shadow overflow-hidden" onClick={() => navigate('/whipped-butter')}>
            <div className="h-48 bg-gradient-to-r from-terracotta-500 to-terracotta-600 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Whipped Shea Butter Collection
                </h3>
                <p className="text-lg">Luxurious Body Butters & Creams</p>
              </div>
            </div>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <p className="text-sage-700">Explore our handcrafted shea butter creations</p>
                <ArrowRight className="w-5 h-5 text-terracotta-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-2xl transition-shadow overflow-hidden" onClick={() => navigate('/body-wash')}>
            <div className="h-48 bg-gradient-to-r from-[#00A86B] to-[#008f5d] flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Body Wash & Soaps
                </h3>
                <p className="text-lg">Whipped Washes & Artisan Bar Soaps</p>
              </div>
            </div>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <p className="text-sage-700">Discover our cleansing collection</p>
                <ArrowRight className="w-5 h-5 text-[#00A86B]" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-2xl transition-shadow overflow-hidden bg-gradient-to-br from-[#81D8D0] to-[#00A86B]" onClick={() => navigate('/body-blend')}>
            <div className="h-48 flex items-center justify-center p-6">
              <div className="text-center text-white">
                <Sparkles className="w-12 h-12 mx-auto mb-2" />
                <h3 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  AI Custom Body Care Builder
                </h3>
                <p className="text-lg">Create Your Perfect Routine</p>
              </div>
            </div>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <p className="text-white font-semibold">AI-powered personalized skincare blends</p>
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
            <Input
              placeholder="Search oils & products..."
              className="pl-10 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="product-search-input"
            />
          </div>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-full md:w-64 bg-white" data-testid="product-type-select">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              <SelectItem value="essential_oil">Essential Oils (100)</SelectItem>
              <SelectItem value="whipped_butter">Whipped Shea Butter (5)</SelectItem>
              <SelectItem value="body_cream">Body Creams (5)</SelectItem>
              <SelectItem value="bar_soap">Bar Soaps (10)</SelectItem>
              <SelectItem value="whipped_soap">Whipped Soaps (5)</SelectItem>
              <SelectItem value="bath_bomb">Bath Bombs (10)</SelectItem>
              <SelectItem value="sugar_scrub">Sugar Scrubs (5)</SelectItem>
              <SelectItem value="strainer">Strainers</SelectItem>
              <SelectItem value="teabag">Tea Bags</SelectItem>
              <SelectItem value="honey">Honey</SelectItem>
              <SelectItem value="tea_set">Tea Sets</SelectItem>
              <SelectItem value="bottle">Bottles</SelectItem>
              <SelectItem value="scoop">Scoops</SelectItem>
              <SelectItem value="storage">Storage</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <p className="text-white mb-6" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
          Showing {filteredProducts.length} products
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredProducts.map(product => (
            <div key={product.id} className="product-card bg-white rounded-xl overflow-hidden shadow-md" data-testid={`product-${product.id}`}>
              <img src={product.image_url} alt={product.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-sage-900 flex-1">{product.name}</h3>
                  {product.type === 'essential_oil' && (
                    <Badge className="bg-sage-600 text-xs ml-2">Oil</Badge>
                  )}
                </div>
                
                {product.scent && (
                  <p className="text-xs text-terracotta-600 font-semibold mb-1">🌸 {product.scent}</p>
                )}
                
                <p className="text-sm text-sage-600 mb-3 line-clamp-2">{product.description}</p>
                
                {product.benefits && product.benefits.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-sage-700 mb-1">Good for:</p>
                    <div className="flex flex-wrap gap-1">
                      {product.benefits.slice(0, 3).map((benefit, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs text-sage-800 border-sage-300 bg-sage-50" data-testid={`benefit-badge-${product.id}-${idx}`}>
                          {benefit}
                        </Badge>
                      ))}
                      {product.benefits.length > 3 && (
                        <Badge variant="outline" className="text-xs text-sage-800 border-sage-300 bg-sage-50">
                          +{product.benefits.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xl font-bold text-terracotta-600">${product.price.toFixed(2)}</span>
                </div>
                <Button className="w-full bg-sage-600 hover:bg-sage-700" onClick={() => addToCart(product.id)}>
                  Add to Cart
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
